import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import useSchools from '../../../hooks/useSchools';
import GradeInput from '../../GradeInput';
import SchoolInput from '../../SchoolInput';
import TextInput from '../../TextInput';
import styles from './GetAccessCode.module.scss';
import formStyles from '../../StudentIdForm/AccessCodeOnlyForm.module.scss';
import { CustomerContext } from '../../../contexts/CustomerContext';
import useQueryString from '../../../hooks/use-query-string';
import { isValidEmailFormat, suggestEmail } from '../../../utils/emailValidation';

// Live-format the phone input as the customer types: strip anything that
// isn't a digit, cap at 10, then re-emit in "(NPA) NXX-XXXX" form. Blocks
// letters entirely and keeps the visible field free of dashes / spaces
// the user would have to insert manually. Backend receives the formatted
// string; digits() extracts the raw 10 when we need to validate.
function formatPhone(input) {
  const digits = (input || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidPhoneFormat(phone) {
  return (phone || '').replace(/\D/g, '').length === 10;
}

// Resend guard-rails. The lookup endpoint mails an Access Code to whatever
// address is posted, so the button that re-triggers it is deliberately
// stingy: a countdown between presses and a hard cap per confirmation
// screen. This is UX politeness on top of the server-side Rack::Attack
// throttle -- it is NOT the security control (see config/initializers/throttle.rb).
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_RESENDS = 2;

// Local, page-scoped strings for the "look up your access code" flow.
// Same pattern as AccessCodeOnlyForm -- retire when the app grows real
// i18n (react-i18next). Keep keys consistent across both dicts.
const STRINGS = {
  en: {
    langToggleLabel: 'Español',
    title: 'Request your Access Code',
    emailLabel: 'Parent email',
    emailPlaceholder: 'you@example.com',
    phoneLabel: 'Phone number',
    phonePlaceholder: '(555) 555-1234',
    schoolLabel: 'School',
    schoolPlaceholder: 'Type your school name',
    firstNameLabel: 'Student’s first name',
    lastNameLabel: 'Student’s last name',
    gradeLabel: 'Grade',
    gradePlaceholder: 'Choose one',
    studentIdLabel: 'Student ID',
    required: '* Required',
    submit: 'Email me my Access Code',
    emailNotePrefix: 'We’ll email your Access Code to ',
    emailNoteFallback: 'the parent email you enter above',
    emailNoteSuffix: '.',
    backLink: '← Back to access code entry',
    // Shown only when the REQUEST itself failed (network, rate limit, 500). A
    // wrong name or an address we don't hold no longer lands here: the lookup
    // answers identically either way, so those reach the confirmation screen.
    requestFailedTitle: 'We couldn’t complete that request.',
    requestFailedBody: 'Something went wrong on our end, or you’ve tried several times in a row. Please wait a minute and try again.',
    notFoundHelp: 'Still stuck? Call us at ',
    notFoundHelpPhone: '(209) 567-0748',
    notFoundHelpOr: ' or ',
    notFoundHelpLink: 'email us for help',
    notFoundHelpEmail: 'info@shoobphoto.com',
    notFoundHelpTrail: '.',
    emailSuggestionPrefix: 'Did you mean ',
    emailSuggestionSuffix: '?',
    emailSuggestionAction: 'Use this',
    emailInvalid: 'That doesn’t look like a complete email address.',
    phoneInvalid: 'Please enter a 10-digit phone number.',
    searchingLabel: 'Sending your Access Code…',
    confirmTitle: 'Check your email',
    // Conditional on purpose, and it has to stay that way. The lookup answers
    // the same for a match, a typo, and an address we don't hold on file -- so
    // this screen appears in all three cases and "We sent your Access Code"
    // would be a plain untruth in two of them.
    confirmBodyPrefix: 'If those details match a student, we’ve sent the Access Code to ',
    confirmBodySuffix: '.',
    // Carries the recovery hint the old "we couldn't find a match" screen used
    // to give, since details that don't match now land here instead of on an
    // error -- the response is deliberately identical either way.
    //
    // Names ONLY the fields the lookup actually compares: school, name and
    // Student ID. Grade is required but never checked, so telling a parent it
    // has to match sends them re-checking the one field that cannot be the
    // problem. The email is not one of them either -- it is where the code is
    // sent, so a typo there means it went elsewhere, not that it wasn't sent.
    confirmSpamNote: 'Give it a few minutes to arrive, and check your spam or junk folder. If nothing turns up, double-check the school, your student’s name and Student ID — they have to match the school’s records exactly — and check the email address for typos. If it still doesn’t arrive, call us and we can help.',
    // The confirmation's primary action. Seeing photos requires typing the
    // code on the entry screen, so that -- not a purchase flow -- is where
    // this button goes.
    confirmBackToEntry: 'Back to Access Code entry',
    resendPrompt: 'Didn’t get it?',
    resendAction: 'Resend',
    resendCooldownLabel: 'Resend in {n}s',
    resendSending: 'Resending…',
    resendSent: 'Sent again — check your inbox.',
    resendError: 'We couldn’t resend just now. Please try again in a minute.',
    resendMaxedPrefix: 'Still nothing? Call us at ',
    resendMaxedOr: ' or ',
    resendMaxedLink: 'email us for help',
    privacyTitle: 'Your Privacy Matters',
    privacyBody: 'Your photos are stored securely and only accessible with your unique code.',
  },
  es: {
    langToggleLabel: 'English',
    title: 'Solicite su código de acceso',
    emailLabel: 'Correo del padre',
    emailPlaceholder: 'usted@ejemplo.com',
    phoneLabel: 'Número de teléfono',
    phonePlaceholder: '(555) 555-1234',
    schoolLabel: 'Escuela',
    schoolPlaceholder: 'Escriba el nombre de su escuela',
    firstNameLabel: 'Nombre del estudiante',
    lastNameLabel: 'Apellido del estudiante',
    gradeLabel: 'Grado',
    gradePlaceholder: 'Elija uno',
    studentIdLabel: 'Identificación del estudiante',
    required: '* Requerido',
    submit: 'Envíenme mi código de acceso',
    emailNotePrefix: 'Enviaremos su código de acceso a ',
    emailNoteFallback: 'el correo del padre que indique arriba',
    emailNoteSuffix: '.',
    backLink: '← Volver al ingreso del código',
    requestFailedTitle: 'No pudimos completar la solicitud.',
    requestFailedBody: 'Algo salió mal de nuestro lado, o lo ha intentado varias veces seguidas. Espere un minuto e inténtelo de nuevo.',
    notFoundHelp: '¿Aún tiene problemas? Llámenos al ',
    notFoundHelpPhone: '(209) 567-0748',
    notFoundHelpOr: ' o ',
    notFoundHelpLink: 'envíenos un correo para obtener ayuda',
    notFoundHelpEmail: 'info@shoobphoto.com',
    notFoundHelpTrail: '.',
    emailSuggestionPrefix: '¿Quiso decir ',
    emailSuggestionSuffix: '?',
    emailSuggestionAction: 'Usar esto',
    emailInvalid: 'Eso no parece una dirección de correo electrónico completa.',
    phoneInvalid: 'Ingrese un número de teléfono de 10 dígitos.',
    searchingLabel: 'Enviando su código de acceso…',
    confirmTitle: 'Revise su correo electrónico',
    confirmBodyPrefix: 'Si esos datos coinciden con un estudiante, enviamos el código de acceso a ',
    confirmBodySuffix: '.',
    confirmSpamNote: 'Puede tardar unos minutos en llegar. Revise su carpeta de spam o correo no deseado. Si no llega nada, verifique la escuela, el nombre de su estudiante y su identificación — deben coincidir exactamente con los registros de la escuela — y revise que el correo electrónico no tenga errores. Si aún así no llega, llámenos y le ayudamos.',
    confirmBackToEntry: 'Volver al ingreso del código de acceso',
    resendPrompt: '¿No lo recibió?',
    resendAction: 'Enviar de nuevo',
    resendCooldownLabel: 'Enviar de nuevo en {n}s',
    resendSending: 'Enviando de nuevo…',
    resendSent: 'Enviado de nuevo — revise su bandeja de entrada.',
    resendError: 'No pudimos enviarlo de nuevo en este momento. Inténtelo en un minuto.',
    resendMaxedPrefix: '¿Aún nada? Llámenos al ',
    resendMaxedOr: ' o ',
    resendMaxedLink: 'envíenos un correo para obtener ayuda',
    privacyTitle: 'Su privacidad es importante',
    privacyBody: 'Sus fotos se almacenan de forma segura y solo son accesibles con su código único.',
  },
};

export default function GetAccessCode() {
  const schools = useSchools();
  const location = useLocation();
  const { customer, setCustomer } = useContext(CustomerContext);
  const { lang } = useQueryString();
  const currentLang = lang === 'es' ? 'es' : 'en';
  const t = STRINGS[currentLang];
  const otherLang = currentLang === 'es' ? 'en' : 'es';
  const langToggleSearch = (() => {
    const params = new URLSearchParams(location.search);
    params.set('lang', otherLang);
    return `?${params.toString()}`;
  })();

  const [email, setEmail] = useState(customer?.email || '');
  const [phone, setPhone] = useState('');
  const [pickedSchool, setPickedSchool] = useState(null);
  // True while pickedSchool holds only the id+name stand-in from the dropdown
  // option, i.e. the pick landed before the school list resolved.
  const [needsFullSchool, setNeedsFullSchool] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [grade, setGrade] = useState('');
  const [availableGrades, setAvailableGrades] = useState([]);
  const [studentId, setStudentId] = useState('');

  const [showRequired, setShowRequired] = useState(false);
  const [status, setStatus] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // "Did you mean X?" suggestion for common domain / TLD typos, computed
  // on blur so it doesn't flicker while the user is still typing.
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  // Independent structural check: on blur, if the email doesn't parse as
  // `local@domain.tld`, show an inline "not a complete email" hint. Set
  // strictly on blur so the user isn't yelled at mid-typing.
  const [emailFormatBad, setEmailFormatBad] = useState(false);
  // Same treatment for phone: format-live-as-you-type + inline "10-digit"
  // hint on blur when the value isn't the required NPA + NXX-XXXX.
  const [phoneFormatBad, setPhoneFormatBad] = useState(false);

  // --- Confirmation screen state ---
  // The address the code actually went to, captured at submit time so the
  // confirmation can't drift if state changes underneath it.
  const [emailedTo, setEmailedTo] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  // 'idle' | 'sending' | 'sent' | 'error'
  const [resendState, setResendState] = useState('idle');

  // Force a clean-slate school selection on this page. Without this, a
  // school stored in CustomerContext from a previous visit would silently
  // apply on submit -- same fix as the AccessCodeOnlyForm.
  useEffect(() => {
    if (customer?.currentSchool) {
      setCustomer({ ...customer, currentSchool: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the not-found banner as the user edits any input, so a stale
  // banner from a previous submit doesn't linger while they're fixing
  // things.
  useEffect(() => {
    if (status === 'BAD') setStatus(undefined);
    setShowRequired(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, phone, pickedSchool?.id, firstName, lastName, grade, studentId]);

  // Counts the resend cooldown down to zero, one second at a time. Guarded
  // on > 0 so it isn't scheduling timeouts while the button is already live.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // NOTE: this page used to resolve the access code to a student/image here
  // and hand off to the purchase flow. It no longer does. The only route to
  // the photos is typing the code on the entry screen, so the lookup, the
  // guardian-affirmation step and the package-selection redirect were all
  // removed rather than left as an unreachable second path.

  // Available grades for the selected school power the Grade dropdown.
  useEffect(() => {
    if (pickedSchool?.id) {
      // `/schools/:id/grades` has never existed -- the only route is the api/v1
      // one, so this 404'd on every school and the dropdown silently fell back
      // to all 15 generic grades. Harmless while grade was cosmetic; not once
      // the lookup started matching on it.
      axios.get(`/api/v1/schools/${pickedSchool.id}/grades`)
        .then((response) => setAvailableGrades(response.data))
        .catch(() => setAvailableGrades([]));
    } else {
      setAvailableGrades([]);
    }
    // Keyed on the id, not the object. `pickedSchool` is replaced by a second
    // object for the SAME school when the stand-in is upgraded to the full
    // record, which fired this fetch twice for one selection.
  }, [pickedSchool?.id]);

  // Same fix as AccessCodeOnlyForm -- see the long note there. SchoolInput
  // runs its own useSchools() fetch, so it can accept a pick before this
  // component's list resolves; looking the option up and storing the result
  // directly dropped the selection and reported "School * Required" over a
  // visibly-filled dropdown.
  const handleSchoolChange = (opt) => {
    if (!opt) {
      setPickedSchool(null);
      setCustomer((c) => ({ ...c, currentSchool: null }));
      return;
    }
    const full = schools.find((s) => String(s.id) === String(opt.value));
    const newSchool = full || { id: opt.value, name: opt.label };
    setNeedsFullSchool(!full);
    setPickedSchool(newSchool);
    setCustomer((c) => ({ ...c, currentSchool: newSchool }));
  };

  // Upgrade the id+name stand-in to the real School record once the list
  // lands, so downstream screens still get accessCodeOnly / schoolTypeId.
  // Gated on the flag, not on comparing records: an identity comparison
  // relies on useSchools returning a referentially stable array, and when it
  // doesn't the comparison never settles and the effect re-sets state forever.
  useEffect(() => {
    if (!needsFullSchool || !pickedSchool || schools.length === 0) return;
    const full = schools.find((s) => String(s.id) === String(pickedSchool.id));
    if (!full) return;
    setPickedSchool(full);
    setCustomer((c) => ({ ...c, currentSchool: full }));
    setNeedsFullSchool(false);
    // Deps are exhaustive: setCustomer is used functionally so this never
    // closes over `customer`, and a re-run after the upgrade returns straight
    // away because the flag is false.
  }, [schools, needsFullSchool, pickedSchool]);

  const missingEmail = showRequired && !email;
  const missingPhone = showRequired && !phone;
  const missingSchool = showRequired && !pickedSchool;
  const missingFirstName = showRequired && !firstName;
  const missingLastName = showRequired && !lastName;
  // GradeInput's "Other" option carries `value: null`, so a plain truthiness
  // test reads a real selection as "nothing picked". `grade` holds exactly what
  // GradeInput gave us -- including that null, so the control can find the
  // option again and keep showing "Other" -- and only the empty initial state
  // counts as unfilled. The null is translated at the payload boundary, not
  // here, because the round-trip through GradeInput is what would break.
  const hasGrade = grade !== '' && grade !== undefined;
  const missingGrade = showRequired && !hasGrade;
  const missingStudentId = showRequired && !studentId;

  // The one lookup request. Both the initial submit and the confirmation's
  // Resend go through here so the posted payload has a single definition.
  const postLookup = () => axios({
    method: 'post',
    url: '/students/search_for_student',
    baseURL: '/',
    data: {
      email,
      phone,
      // The server only checks that a grade is PRESENT, so the "Other"
      // option's null becomes a marker here rather than in state.
      grade: grade ?? 'OTHER',
      school_id: pickedSchool.id,
      student_first_name: firstName,
      student_last_name: lastName,
      student_id: studentId,
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // All seven are required.
    if (!email || !phone || !pickedSchool || !firstName || !lastName || !hasGrade || !studentId) {
      setShowRequired(true);
      setStatus(undefined);
      return;
    }
    // Also gate submit on structural email + phone validity so we don't
    // send an obviously-broken value to the lookup.
    if (!isValidEmailFormat(email)) {
      setEmailFormatBad(true);
      setStatus(undefined);
      return;
    }
    if (!isValidPhoneFormat(phone)) {
      setPhoneFormatBad(true);
      setStatus(undefined);
      return;
    }
    setShowRequired(false);
    setIsSubmitting(true);

    // Any 2xx means "we've dealt with it" -- the lookup deliberately does not
    // report whether a student matched, so there is nothing to branch on. Only
    // a transport-level failure (offline, rate limited, 500) is an error here.
    postLookup()
      .then(() => {
        setStatus('OK');
        setEmailedTo(email);
        // Make them wait out one cooldown before the first resend, so the
        // confirmation can't be turned into a send-button on arrival.
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setStatus('BAD');
      })
      .finally(() => {
        setIsSubmitting(false);
        window.scrollTo(0, 0);
      });
  };

  // Re-fires the exact same lookup, which re-sends the email. Reuses
  // postLookup rather than a second copy of the request so the payload can
  // never drift from the original submit.
  const handleResend = () => {
    if (resendCooldown > 0 || resendCount >= MAX_RESENDS || resendState === 'sending') return;

    setResendState('sending');
    postLookup()
      .then(() => {
        setResendState('sent');
        setResendCount((c) => c + 1);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => setResendState('error'));
  };

  // Confirmation: the email has been sent. This is the terminal screen for
  // the request flow -- the code is in their inbox, and the way to the photos
  // is back through the entry screen.
  if (status === 'OK') {
    const resendExhausted = resendCount >= MAX_RESENDS;

    return (
      <div className={formStyles.page} lang={currentLang}>
        <div className={formStyles.card}>
          <Link
            to={{ pathname: location.pathname, search: langToggleSearch }}
            className={formStyles.langToggle}
            translate="no"
            hrefLang={otherLang}
          >
            {t.langToggleLabel}
          </Link>

          <div className={styles.successBadge} aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1 className={formStyles.title}>{t.confirmTitle}</h1>

          {/* role=status so a screen reader announces the outcome on arrival */}
          <p className={styles.confirmBody} role="status">
            {t.confirmBodyPrefix}
            <strong className={styles.confirmAddress}>{emailedTo}</strong>
            {t.confirmBodySuffix}
          </p>

          <p className={styles.confirmSpamNote}>{t.confirmSpamNote}</p>

          {/* Styled as the primary CTA but it IS the navigation -- a Link, so
              it works with cmd-click and shows a real href on hover. */}
          <Link
            to={{ pathname: '/student_id_entry', search: `?lang=${currentLang}` }}
            className={`${formStyles.cta} ${styles.confirmCta}`}
          >
            {t.confirmBackToEntry}
          </Link>

          <div className={styles.resendRow}>
            {resendState === 'sent' && (
              <p className={styles.resendSent} role="status">{t.resendSent}</p>
            )}
            {resendState === 'error' && (
              <p className={styles.resendError} role="alert">{t.resendError}</p>
            )}

            {resendExhausted ? (
              <p className={styles.resendHelp}>
                {t.resendMaxedPrefix}
                <a href={`tel:${t.notFoundHelpPhone.replace(/[^\d]/g, '')}`}>{t.notFoundHelpPhone}</a>
                {t.resendMaxedOr}
                <a href="/contact">{t.resendMaxedLink}</a>
                {t.notFoundHelpTrail}
              </p>
            ) : (
              <p className={styles.resendPrompt}>
                {t.resendPrompt}
                {' '}
                <button
                  type="button"
                  className={styles.resendAction}
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendState === 'sending'}
                >
                  {resendState === 'sending' && t.resendSending}
                  {resendState !== 'sending' && resendCooldown > 0
                    && t.resendCooldownLabel.replace('{n}', resendCooldown)}
                  {resendState !== 'sending' && resendCooldown === 0 && t.resendAction}
                </button>
              </p>
            )}
          </div>
        </div>

        <div className={formStyles.privacyCallout}>
          <div className={formStyles.privacyTitle}>{t.privacyTitle}</div>
          <div className={formStyles.privacyBody}>{t.privacyBody}</div>
        </div>
      </div>
    );
  }

  const requestFailed = status === 'BAD';

  return (
    <div className={formStyles.page} lang={currentLang}>
      {/* .lookupCard tightens the padding on phones only -- seven fields is a
          tall card and the submit button was falling below the fold. */}
      <div className={`${formStyles.card} ${styles.lookupCard}`}>
        <Link
          to={{ pathname: location.pathname, search: langToggleSearch }}
          className={formStyles.langToggle}
          translate="no"
          hrefLang={otherLang}
        >
          {t.langToggleLabel}
        </Link>

        <div className={`${formStyles.iconBadge} ${styles.lookupBadge}`} aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="8" r="4" />
            <path d="M2 21c0-4.4 3.6-8 8-8 1.4 0 2.7.4 3.8 1" />
            <circle cx="17.5" cy="17.5" r="3.5" />
            <line x1="20.5" y1="20.5" x2="23" y2="23" />
          </svg>
        </div>
        <h1 className={`${formStyles.title} ${styles.brandTitle}`}>{t.title}</h1>

        {requestFailed && (
          <div className={formStyles.errorBanner} role="alert">
            <span className={formStyles.errorIcon} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <div className={formStyles.errorText}>
              <div className={formStyles.errorTitle}>{t.requestFailedTitle}</div>
              <div className={formStyles.errorBody}>{t.requestFailedBody}</div>
              <div className={formStyles.errorAction}>
                {t.notFoundHelp}
                <a href={`tel:${t.notFoundHelpPhone.replace(/[^\d]/g, '')}`}>{t.notFoundHelpPhone}</a>
                {t.notFoundHelpOr}
                <a href="/contact" className={styles.errorEmailLink}>
                  <span className={styles.errorEmailIcon} aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  {t.notFoundHelpLink}
                </a>
                {t.notFoundHelpTrail}
              </div>
            </div>
          </div>
        )}

        <form className={`${formStyles.form} ${styles.lookupForm}`} onSubmit={handleSubmit} noValidate>
          <div className={`${formStyles.field} ${missingEmail ? formStyles.errorField : ''}`}>
            <label htmlFor="ac-email">
              {t.emailLabel}
              {missingEmail && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
            </label>
            <div className={formStyles.inputWithIcon}>
              <span className={formStyles.leadingIcon} aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <TextInput
                id="ac-email"
                val={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailSuggestion) setEmailSuggestion(null);
                  if (emailFormatBad) setEmailFormatBad(false);
                }}
                onBlur={() => {
                  if (!email) return;
                  setEmailFormatBad(!isValidEmailFormat(email));
                  setEmailSuggestion(suggestEmail(email));
                }}
                type="email"
                autoComplete="email"
                placeholder={t.emailPlaceholder}
                aria-required="true"
                aria-invalid={emailFormatBad || undefined}
              />
            </div>
            {emailFormatBad && (
              <div className={formStyles.errorHint}>{t.emailInvalid}</div>
            )}
            {!emailFormatBad && emailSuggestion && (
              <div className={styles.emailSuggestion}>
                {t.emailSuggestionPrefix}
                <button
                  type="button"
                  className={styles.emailSuggestionAction}
                  onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(null); }}
                >
                  {emailSuggestion}
                </button>
                {t.emailSuggestionSuffix}
              </div>
            )}
          </div>

          <div className={`${formStyles.field} ${(missingPhone || phoneFormatBad) ? formStyles.errorField : ''}`}>
            <label htmlFor="ac-phone">
              {t.phoneLabel}
              {missingPhone && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
            </label>
            <div className={formStyles.inputWithIcon}>
              <span className={formStyles.leadingIcon} aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              <TextInput
                id="ac-phone"
                val={phone}
                onChange={(e) => {
                  setPhone(formatPhone(e.target.value));
                  if (phoneFormatBad) setPhoneFormatBad(false);
                }}
                onBlur={() => {
                  if (!phone) return;
                  setPhoneFormatBad(!isValidPhoneFormat(phone));
                }}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t.phonePlaceholder}
                aria-required="true"
                aria-invalid={phoneFormatBad || undefined}
                maxLength={14}
              />
            </div>
            {phoneFormatBad && (
              <div className={formStyles.errorHint}>{t.phoneInvalid}</div>
            )}
          </div>

          <div className={`${formStyles.field} ${missingSchool ? formStyles.errorField : ''}`}>
            <label htmlFor="school-input">
              {t.schoolLabel}
              {missingSchool && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
            </label>
            <SchoolInput
              onChange={handleSchoolChange}
              hideLabel
              noDefault
              openOnTypeOnly
              placeholder={t.schoolPlaceholder}
              error={missingSchool}
            />
          </div>

          <div className={styles.fieldRow}>
            <div className={`${formStyles.field} ${missingFirstName ? formStyles.errorField : ''}`}>
              <label htmlFor="ac-fname">
                {t.firstNameLabel}
                {missingFirstName && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
              </label>
              <TextInput
                id="ac-fname"
                val={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                aria-required="true"
              />
            </div>
            <div className={`${formStyles.field} ${missingLastName ? formStyles.errorField : ''}`}>
              <label htmlFor="ac-lname">
                {t.lastNameLabel}
                {missingLastName && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
              </label>
              <TextInput
                id="ac-lname"
                val={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                aria-required="true"
              />
            </div>
          </div>

          <div className={`${styles.fieldRow} ${styles.fieldRowPair}`}>
            <div className={`${formStyles.field} ${missingGrade ? formStyles.errorField : ''}`}>
              <label>
                {t.gradeLabel}
                {missingGrade && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
              </label>
              <GradeInput
                val={grade}
                onChange={setGrade}
                availableGrades={availableGrades}
                error={missingGrade}
                placeholder={t.gradePlaceholder}
                short
                menuPlacement="top"
              />
            </div>
            <div className={`${formStyles.field} ${missingStudentId ? formStyles.errorField : ''}`}>
              <label htmlFor="ac-sid">
                {t.studentIdLabel}
                {missingStudentId && <span className={formStyles.required} aria-hidden="true">{t.required}</span>}
              </label>
              <TextInput
                id="ac-sid"
                val={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                aria-required="true"
              />
            </div>
          </div>

          {/* Restates the outcome of pressing the button -- and, once the
              email field holds something valid, names the exact address the
              code is going to, so a typo is visible before they submit. */}
          <p className={styles.emailNote}>
            <span className={styles.emailNoteIcon} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </span>
            <span>
              {t.emailNotePrefix}
              {isValidEmailFormat(email)
                ? <strong className={styles.emailNoteAddress}>{email}</strong>
                : t.emailNoteFallback}
              {t.emailNoteSuffix}
            </span>
          </p>

          <button type="submit" className={formStyles.cta} disabled={isSubmitting}>
            {isSubmitting ? t.searchingLabel : t.submit}
          </button>
        </form>

        <p className={formStyles.secondaryRow}>
          <Link to={{ pathname: '/student_id_entry', search: `?lang=${currentLang}` }}>{t.backLink}</Link>
        </p>

      </div>

      <div className={formStyles.privacyCallout}>
        <div className={formStyles.privacyTitle}>{t.privacyTitle}</div>
        <div className={formStyles.privacyBody}>{t.privacyBody}</div>
      </div>
    </div>
  );
}
